// All locations on one screen — the numbers a multi-location operator
// otherwise checks store by store: low stock, what needs ordering, open POs,
// upcoming production, CPG receivables, today's sales. Click a venue card to
// work inside that venue.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import api, { authApi } from '../services/api';

interface VenueRow {
  id: string;
  name: string;
  address?: string;
  lowStock: number;
  lowNames: string[];
  suggestedOrders: number;
  openPOs: number;
  productionUnits: number;
  cpgOpenOrders: number;
  cpgOwedCents: number;
  salesTodayCents: number;
  salesWeekCents?: number;
}

const money = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

export default function LocationsPage() {
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const currentId = (() => {
    try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || '{}')?.id; } catch { return null; }
  })();

  const { data, isLoading } = useQuery({
    queryKey: ['owner-overview'],
    queryFn: async () => (await api.get('/v1/owner/overview')).data?.data as {
      venues: VenueRow[]; totals: VenueRow | null;
      priceMoves?: Array<{ venue: string; item: string; fromCents: number; toCents: number; pct: number; venueCount?: number }>;
      productionLanes?: Array<{ venue: string; destination: string; units: number }>;
      cpgReceivables?: Array<{ venue: string; customer: string; orderNumber: string; cents: number }>;
    },
  });

  const openVenue = async (restaurantId: string) => {
    if (switchingId) return;
    if (restaurantId === currentId) { window.location.href = '/'; return; }
    setSwitchingId(restaurantId);
    try {
      const response = await authApi.switchRestaurant(restaurantId);
      const { accessToken, refreshToken, restaurant } = response.data.data;
      localStorage.setItem('manager_token', accessToken);
      localStorage.setItem('manager_refresh_token', refreshToken);
      localStorage.setItem('manager_current_restaurant', JSON.stringify(restaurant));
      window.location.href = '/';
    } catch {
      setSwitchingId(null);
    }
  };

  const venues = data?.venues || [];
  const totals = data?.totals;

  if (isLoading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>;
  }

  const heroStats: Array<[string, string]> = totals
    ? [
        ['Locations', String(venues.length)],
        ...(totals.salesTodayCents > 0 ? [['Sales today', money(totals.salesTodayCents)] as [string, string]] : []),
        ...(((totals as any).salesWeekCents || 0) > 0 ? [['Sales · 7 days', money((totals as any).salesWeekCents)] as [string, string]] : []),
        ['Items low', String(totals.lowStock)],
        ['Orders to place', String(totals.suggestedOrders)],
        ['Open POs', String(totals.openPOs)],
        ['In production · next 2 days', totals.productionUnits.toLocaleString('en-US')],
        ...(totals.cpgOwedCents > 0 ? [['CPG owed', money(totals.cpgOwedCents)] as [string, string]] : []),
      ]
    : [];

  const lanes = data?.productionLanes || [];
  const maxLaneUnits = lanes.reduce((max, l) => Math.max(max, l.units), 0);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">All locations</h1>
        <p className="text-slate-500">Every venue at a glance — click one to work inside it.</p>
      </div>

      {totals && (
        <div className="card p-6 lg:p-8">
          <div className="flex flex-wrap items-stretch gap-y-6 divide-x divide-slate-100">
            {heroStats.map(([label, value]) => (
              <div key={label} className="px-6 lg:px-8 first:pl-0">
                <p className="text-3xl font-semibold text-slate-900 tabular-nums">{value}</p>
                <p className="text-xs text-slate-500 mt-1.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {venues.map((v) => {
          const chips = [
            ...(v.salesTodayCents > 0 ? [{ label: 'Sales today', value: v.salesTodayCents, display: money(v.salesTodayCents) }] : []),
            ...((v.salesWeekCents || 0) > 0 ? [{ label: 'Sales · 7d', value: v.salesWeekCents!, display: money(v.salesWeekCents!) }] : []),
            { label: 'Low stock', value: v.lowStock, display: String(v.lowStock) },
            { label: 'To order', value: v.suggestedOrders, display: String(v.suggestedOrders) },
            { label: 'Open POs', value: v.openPOs, display: String(v.openPOs) },
            { label: 'Production', value: v.productionUnits, display: v.productionUnits.toLocaleString('en-US') },
          ];
          return (
            <div
              key={v.id}
              role="button"
              tabIndex={0}
              onClick={() => openVenue(v.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') openVenue(v.id); }}
              className="card group p-5 cursor-pointer transition hover:border-slate-300 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-slate-900 truncate">{v.name}</h2>
                    {v.id === currentId && <span className="badge badge-info">current</span>}
                  </div>
                  {v.address && <p className="text-xs text-slate-400 mt-0.5 truncate">{v.address}</p>}
                </div>
                {switchingId === v.id
                  ? <Loader2 className="w-4 h-4 animate-spin text-slate-400 shrink-0 mt-1" />
                  : <ArrowRight className="w-4 h-4 text-slate-300 shrink-0 mt-1 transition-all group-hover:text-slate-500 group-hover:translate-x-0.5" />}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
                {chips.map((chip) => (
                  <div key={chip.label} className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className={clsx('text-sm font-semibold tabular-nums', chip.value === 0 ? 'text-slate-400' : 'text-slate-900')}>
                      {chip.display}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{chip.label}</p>
                  </div>
                ))}
              </div>

              {v.lowNames.length > 0 && (
                <p className="flex items-center gap-1.5 text-xs text-slate-500 mt-3 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                  <span className="truncate">Low: {v.lowNames.join(', ')}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>

      {(lanes.length > 0 || (data?.priceMoves?.length || 0) > 0 || (data?.cpgReceivables?.length || 0) > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {lanes.length > 0 && (
            <div className="card p-5">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">In production · next 2 days</p>
              <div className="space-y-3">
                {lanes.map((l, i) => (
                  <div key={i}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm text-slate-700 truncate">{l.destination}</span>
                      <span className="text-sm font-semibold tabular-nums text-slate-900">{l.units.toLocaleString('en-US')}</span>
                    </div>
                    <div className="h-1 rounded-full bg-primary-100 mt-1.5 overflow-hidden">
                      <div
                        className="h-1 rounded-full bg-primary-500"
                        style={{ width: `${maxLaneUnits ? Math.max(4, Math.round((l.units / maxLaneUnits) * 100)) : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
                <div className="flex items-baseline justify-between border-t border-slate-100 pt-2">
                  <span className="text-sm text-slate-500">Total units</span>
                  <span className="text-sm font-bold tabular-nums text-slate-900">
                    {lanes.reduce((s, l) => s + l.units, 0).toLocaleString('en-US')}
                  </span>
                </div>
              </div>
            </div>
          )}
          {(data?.priceMoves?.length || 0) > 0 && (
            <div className="card p-5">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Price moves · last 14 days</p>
              <div className="space-y-2.5">
                {data!.priceMoves!.map((m, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-sm text-slate-700">{m.item}</span>
                      <span className="text-xs text-slate-400 ml-1.5">{(m.venueCount || 1) > 1 ? `${m.venueCount} locations` : m.venue}</span>
                    </div>
                    <span className="flex items-baseline gap-1.5 text-sm tabular-nums whitespace-nowrap">
                      <span className="text-slate-400">{money(m.fromCents)} →</span>
                      <span className="font-semibold text-slate-900">{money(m.toCents)}</span>
                      <span className={clsx(
                        'rounded px-1.5 py-0.5 text-xs font-medium',
                        m.pct > 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700',
                      )}>
                        {m.pct > 0 ? '+' : ''}{m.pct}%
                      </span>
                    </span>
                  </div>
                ))}
                <p className="text-xs text-slate-400 pt-1">Booked automatically at receiving — recipe costs moved with them.</p>
              </div>
            </div>
          )}
          {(data?.cpgReceivables?.length || 0) > 0 && (
            <div className="card p-5">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">CPG receivables</p>
              <div className="space-y-2.5">
                {data!.cpgReceivables!.map((rcv, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-2">
                    <div className="flex items-baseline gap-1.5 min-w-0">
                      <span className="text-sm text-slate-700 truncate">{rcv.customer}</span>
                      <span className="rounded bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-400 whitespace-nowrap">
                        {rcv.orderNumber}
                      </span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums text-slate-900">{money(rcv.cents)}</span>
                  </div>
                ))}
                <p className="text-xs text-slate-400 pt-1">Invoiced and unpaid — from the wholesale ledger.</p>
              </div>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-slate-400">
        Numbers come from each venue's own data — inventory pars, purchase orders, production schedule, wholesale invoices.
        A quiet zero means nothing needs attention there.
      </p>
    </div>
  );
}
